import React from 'react';

export function SettingsSystemTab({ p }) {
    return (
                <div className="settings-tab-content">
                    <div className="settings-callout" style={{ marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: 6, background: 'var(--bg-secondary, #f5f5f5)', border: '1px solid var(--border-color, #ddd)' }}>
                        <strong>Instance System Settings</strong> — deployment-wide configuration for this Zagger server (embedding model, shared ingestion API credentials, runtime diagnostics).
                        This is <em>not</em> the project chat agent. To change how an agent behaves in a project, open <strong>Project settings → Agent Settings</strong> and edit the agent system message.
                    </div>
                    {/* Ingestion Groups section moved to Main page */}

                    {p.systemSettingsLoading && (
                        <div className="loading">
                            <i className="fas fa-spinner fa-spin"></i> Loading system settings...
                        </div>
                    )}

                    {/* Supabase Settings - Read-only display */}
                    <div className="settings-category" id="supabase-category">
                        <h3>
                            <i className="fas fa-database"></i> Supabase Configuration
                            <span className="badge badge-api">API</span>
                        </h3>
                        <div id="supabase-settings">
                            <div className="setting-item">
                                <div>
                                    <div className="setting-label">Supabase URL</div>
                                    <div className="setting-description">Your Supabase project URL (configured on
                                        server)
                                    </div>
                                </div>
                                <div className="setting-value">
                                    <input
                                        type="text"
                                        value="Loaded from API server"
                                        readOnly
                                        placeholder="Loaded from API server"
                                        className="readonly-input"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Runtime Environment (Debug) - Read-only display */}
                    <div className="settings-category" id="runtime-env-category">
                        <h3>
                            <i className="fas fa-plug"></i> Runtime Environment
                            <span className="badge badge-config">DEBUG</span>
                        </h3>
                        <div id="runtime-env-settings">
                            <div className="setting-item">
                                <div>
                                    <div className="setting-label">API Base URI</div>
                                    <div className="setting-description">Resolved API endpoint base that the
                                        frontend uses for requests
                                    </div>
                                </div>
                                <div className="setting-value">
                                    <input
                                        type="text"
                                        value={p.runtimeInfo.apiUri}
                                        readOnly
                                        className="readonly-input"
                                    />
                                </div>
                            </div>
                            <div className="setting-item">
                                <div>
                                    <div className="setting-label">Root Domain</div>
                                    <div className="setting-description">Current host/domain for this app
                                    </div>
                                </div>
                                <div className="setting-value">
                                    <input
                                        type="text"
                                        value={p.runtimeInfo.rootDomain}
                                        readOnly
                                        className="readonly-input"
                                    />
                                </div>
                            </div>
                            <div className="setting-item">
                                <div>
                                    <div className="setting-label">Root Path</div>
                                    <div className="setting-description">Configured root path (protocol + domain + base path)
                                    </div>
                                </div>
                                <div className="setting-value">
                                    <input
                                        type="text"
                                        value={p.runtimeInfo.rootPath}
                                        readOnly
                                        className="readonly-input"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="settings-category" id="ai-runtime-category">
                        <h3>
                            <i className="fas fa-robot"></i> AI Runtime
                            <span className="badge badge-api">AI</span>
                        </h3>
                        <div id="ai-runtime-settings">
                            <div className="setting-item">
                                <div>
                                    <div className="setting-label">Active Runtime</div>
                                    <div className="setting-description">
                                        Current server runtime mode reported by health endpoint (AI SDK is the active runtime)
                                    </div>
                                </div>
                                <div className="setting-value">
                                    <input
                                        type="text"
                                        value={p.systemSettingsLoading
                                            ? 'Loading...'
                                            : (p.systemSettings.aiRuntime || 'langchain')}
                                        readOnly
                                        className="readonly-input"
                                    />
                                </div>
                            </div>
                            <div className="setting-item">
                                <div>
                                    <div className="setting-label">Runtime Health</div>
                                    <div className="setting-description">Server health status associated with runtime detection</div>
                                </div>
                                <div className="setting-value">
                                    <input
                                        type="text"
                                        value={p.systemSettingsLoading
                                            ? 'Loading...'
                                            : `${p.systemSettings.aiRuntimeStatus || 'unknown'}${p.systemSettings.aiRuntimeHealth === null ? '' : (p.systemSettings.aiRuntimeHealth ? ' (healthy)' : ' (degraded)')}`}
                                        readOnly
                                        className="readonly-input"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Embedding Configuration */}
                    <div className={`settings-category ${p.showEmbeddingEditGate ? 'hidden-by-modal' : ''}`} id="embedding-category">
                        <h3>
                            <i className="fas fa-layer-group"></i> Embedding Configuration
                            <span className="badge badge-api">AI</span>
                        </h3>
                        <div className="setting-item">
                            <div>
                                <div className="setting-label">Embedding Model</div>
                                <div className="setting-description">
                                    Select the embedding model to use for vectorization. Changing this requires recalculating all embeddings.
                                </div>
                            </div>
                            <div className="setting-value">
                                <div className="form-group model-combobox">
                                    <input
                                        type="text"
                                        id="embedding-model"
                                        ref={p.embeddingModelInputRef}
                                        value={p.embeddingModelQuery}
                                        onMouseDown={(e) => {
                                            // Gate the very first interaction until acknowledged
                                            if (!p.embeddingEditGateAcknowledgedRef.current) {
                                                e.preventDefault();
                                                p.setShowEmbeddingEditGate(true);
                                            }
                                        }}
                                        onChange={(e) => {
                                            // If gate not acknowledged yet, ignore edits and re-open gate
                                            if (!p.embeddingEditGateAcknowledgedRef.current) {
                                                p.setShowEmbeddingEditGate(true);
                                                return;
                                            }
                                            p.setEmbeddingModelQuery(e.target.value);
                                            p.setShowEmbeddingModelList(true);
                                            p.setEmbeddingModelHighlight(0);
                                        }}
                                        onFocus={(e) => {
                                            if (!p.embeddingEditGateAcknowledgedRef.current) {
                                                // Immediately blur and show gate; user must acknowledge first
                                                e.target.blur();
                                                p.setShowEmbeddingEditGate(true);
                                                return;
                                            }
                                            p.setShowEmbeddingModelList(true);
                                        }}
                                        onBlur={() => {
                                            // If user typed an exact match and blurs, treat it as a selection
                                            const val = (p.embeddingModelQuery || '').trim().toLowerCase();
                                            if (!val) return;
                                            const exact = (p.embeddingModels || []).find(m => {
                                                const name = (m.name || '').toLowerCase();
                                                const id = (m.id || '').toLowerCase();
                                                return name === val || id === val;
                                            });
                                            if (exact) {
                                                p.handleSelectEmbeddingModel(exact);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (!p.showEmbeddingModelList) return;
                                            const maxIndex = Math.max(0, (p.filteredEmbeddingModels || []).length - 1);
                                            if (e.key === 'ArrowDown') { e.preventDefault(); p.setEmbeddingModelHighlight(h => Math.min(h + 1, maxIndex)); }
                                            if (e.key === 'ArrowUp') { e.preventDefault(); p.setEmbeddingModelHighlight(h => Math.max(h - 1, 0)); }
                                            if (e.key === 'Enter') { e.preventDefault(); const m = p.filteredEmbeddingModels[p.embeddingModelHighlight]; if (m) { p.handleSelectEmbeddingModel(m); } }
                                            if (e.key === 'Escape') { p.setShowEmbeddingModelList(false); }
                                        }}
                                        placeholder={(() => {
                                            const curr = (p.embeddingModels || []).find(m => m.id === p.systemSettings.embeddingModel);
                                            return curr ? `${curr.name || curr.id}` : (p.systemSettings.embeddingModel || 'Select an embedding model');
                                        })()}
                                        disabled={p.systemSettingsLoading || p.embeddingModelsLoading || p.saving}
                                    />
                                    {p.showEmbeddingModelList && (
                                        <div className="model-dropdown" ref={p.embeddingModelListRef}>
                                            {p.filteredEmbeddingModels.length === 0 && (
                                                <div className="model-item disabled">{p.embeddingModelsLoading ? 'Loading models…' : 'No models'}</div>
                                            )}
                                            {p.filteredEmbeddingModels.map((m, idx) => (
                                                <div
                                                    key={m.id}
                                                    className={`model-item ${idx === p.embeddingModelHighlight ? 'active' : ''}`}
                                                    onMouseEnter={() => p.setEmbeddingModelHighlight(idx)}
                                                    onMouseDown={(e) => { e.preventDefault(); p.handleSelectEmbeddingModel(m); }}
                                                >
                                                    <div className="model-name">{m.name || m.id}</div>
                                                    <div className="model-meta">{m.provider}{m.dimensions ? ` • ${m.dimensions} dims` : ''}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Embedding Edit Gate Modal (standard size, like other modals) */}
                    {p.showEmbeddingEditGate && (
                        <div className="modal show">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h2 className="modal-title">Important: Changing the Embedding Model</h2>
                                    <span className="close" onClick={() => p.setShowEmbeddingEditGate(false)}>&times;</span>
                                </div>
                                <div className="modal-body">
                                    <div className="alert alert-warning" style={{marginBottom: '16px'}}>
                                        <i className="fas fa-exclamation-triangle"></i>
                                        Changing the embedding model requires recalculating all existing embeddings. This can be time-consuming and search quality may degrade until re-indexing completes.
                                    </div>
                                    <div className="alert alert-info" style={{marginBottom: '16px'}}>
                                        <i className="fas fa-info-circle"></i>
                                        Your change will not be saved until you click "Save System Settings."
                                    </div>
                                    <p>You must acknowledge this warning before editing the embedding configuration.</p>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary" onClick={() => p.setShowEmbeddingEditGate(false)}>
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            p.embeddingEditGateAcknowledgedRef.current = true;
                                            p.setShowEmbeddingEditGate(false);
                                            // After acknowledging, focus the input and open the dropdown
                                            setTimeout(() => {
                                                if (p.embeddingModelInputRef.current) {
                                                    p.embeddingModelInputRef.current.focus();
                                                }
                                                p.setShowEmbeddingModelList(true);
                                            }, 0);
                                        }}
                                    >
                                        <i className="fas fa-check"></i> I Understand, Continue
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Save System Settings Button */
                    }
                    <div className="settings-save-section">
                        <button
                            className="btn btn-primary btn-save-settings"
                            onClick={p.handleSaveSystemSettings}
                            disabled={!p.hasTabChanges() || p.saving || p.systemSettingsLoading || !p.user?.email}
                        >
                            {p.saving ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i> Saving...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-save"></i> Save System Settings
                                </>
                            )}
                        </button>
                        {p.hasTabChanges() && (
                            <div className="unsaved-changes-indicator">
                                <i className="fas fa-exclamation-circle"></i> You have unsaved changes
                            </div>
                        )}
                    </div>
                </div>
    );
}
